import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Sparkles,
  PanelRightClose,
  BookmarkPlus,
  Stethoscope,
  Layers,
  Trash2,
  X,
  Check,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { Button, Card, CardBody, cn } from '@insolech/ui';
import { prescriptionPresetQueries } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';
import type { PrescriptionFormState } from './PrescriptionForm';
import type { PrescriptionDeepState } from './prescription/prescriptionState';
import {
  summarizeModifications,
  type ModEntry,
} from './prescription/summary';
import { getPathologyPresets } from './prescription/pathologyPresets';
import { stripPresetCustomName } from './presetSanitize';

/**
 * PrescriptionAssistantPanel (R33 rebuild).
 *
 * Dropdown-driven assistant (intentionally different from the form's
 * section-by-section UI). Two modes:
 *   • My presets  - clinic-scoped saved prescriptions; dropdown loads one.
 *   • By pathology - curated pathology defaults; dropdown layers one onto
 *     the current prescription.
 *
 * Once an option is applied, the dropdown collapses to a brand-tinted
 * applied chip with an × to clear it. Applying a preset or a pathology
 * MERGES its modifications on top of the baseline (form.deep at the moment
 * of the first apply). Switching to a different option re-merges on top of
 * the SAME baseline, so the pathology contribution is swapped while the
 * pre-default cards survive. Clicking the X restores form.deep to the
 * captured baseline. Manual tweaks made after apply are intentionally
 * dropped on clear or on switch, mirroring the replace semantics used when
 * switching between presets.
 *
 * A live "Currently selected" panel below the picker shows chips for top
 * cover + every filled modification card; clicking a chip scrolls to that
 * card in the form (using the cardId returned by the summary helper).
 *
 * The collapsed/expanded state is controlled by the parent (NewOrderPage)
 * so a "Show assistant" button can sit next to the stepper card when the
 * panel is hidden.
 */

/** Shown under the save controls when the insert fails (RLS, network, a
 *  clinic the user cannot write to). Cleared on the next attempt. */
const PRESET_SAVE_ERROR = 'Could not save the preset for this clinic.';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/**
 * Deep-merge a patch onto a baseline PrescriptionDeepState. Plain-object
 * keys recurse; arrays and primitives are replaced wholesale (patch wins).
 * This keeps pre-default cards alive when a pathology only writes into a
 * sibling subtree (e.g. patch writes rearfoot.subcalcaneal_offloading
 * while baseline has rearfoot.rearfoot_posting).
 *
 * Exported for the unit test that proves a baseline design key survives a
 * preset apply (the preset patch never carries one); nothing else imports it.
 */
export function deepMergeDeepState(
  base: PrescriptionDeepState,
  patch: PrescriptionDeepState,
): PrescriptionDeepState {
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [key, patchValue] of Object.entries(patch as Record<string, unknown>)) {
    if (patchValue === undefined) continue;
    const baseValue = out[key];
    if (isPlainObject(baseValue) && isPlainObject(patchValue)) {
      out[key] = deepMergeDeepState(
        baseValue as PrescriptionDeepState,
        patchValue as PrescriptionDeepState,
      );
    } else {
      out[key] = patchValue;
    }
  }
  return out as PrescriptionDeepState;
}

interface Props {
  form: PrescriptionFormState;
  /** Replace the form's deep modification state. */
  onApplyDeep: (next: PrescriptionDeepState) => void;
  /** Parent-controlled collapse state - defaults open. */
  collapsed: boolean;
  onCollapsedChange: (next: boolean) => void;
  /** Parent-provided: open the card's parent section, then scroll to it. */
  onJumpToCard?: ((cardId: string) => void) | undefined;
  /**
   * When true, the pathology picker uses the sandal-form variants of the
   * canonical presets (intrinsic-only, no flanges, heel_offloading instead
   * of heel_cutout). Defaults to false so the insole flow stays unchanged.
   */
  isSandal?: boolean;
  /**
   * The clinic the presets belong to. Presets are CLINIC-owned
   * (prescription_presets.clinic_id, RLS `is_admin() OR is_clinic_member`),
   * and useAuth().clinicId is only the user's DEFAULT clinic, which is null
   * for admins. The wizard and the edit dialog know the clinic the order is
   * for, so they pass it here; when non-null it wins over the auth default.
   */
  clinicId?: string | null;
}

type Tab = 'presets' | 'pathology';

interface AppliedSelection {
  kind: 'preset' | 'pathology';
  id: string;
  label: string;
}

export function PrescriptionAssistantPanel({
  form,
  onApplyDeep,
  collapsed,
  onCollapsedChange,
  onJumpToCard,
  isSandal = false,
  clinicId: clinicIdProp,
}: Props) {
  const auth = useAuth();
  // The order's clinic (prop) wins when the caller knows it; the auth default
  // is the fallback for callers that do not thread one. Both are null for an
  // admin outside an order, in which case saving stays disabled below.
  const clinicId = clinicIdProp ?? auth.clinicId;
  const user = auth.user;
  const presets = getPathologyPresets(isSandal);
  const queryClient = useQueryClient();
  const [tab, setTab] = React.useState<Tab>('presets');
  const [savingName, setSavingName] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [applied, setApplied] = React.useState<AppliedSelection | null>(null);
  const [snapshotBeforeApply, setSnapshotBeforeApply] = React.useState<PrescriptionDeepState | null>(null);

  const presetsQuery = useQuery(
    prescriptionPresetQueries.prescriptionPresetsQuery(supabase, clinicId),
  );

  const createMutation = useMutation({
    mutationFn: (name: string) => {
      // canSave keeps the button disabled without a clinic; this guard is for
      // a programmatic mutate, so the insert can never go out with a null
      // clinic_id (NOT NULL column, and the RLS check needs a real clinic).
      if (!clinicId) {
        throw new Error(
          'Cannot save a preset without a clinic. Open the assistant from an order with a clinic selected.',
        );
      }
      return prescriptionPresetQueries.createPrescriptionPreset(supabase, {
        clinic_id: clinicId,
        created_by: user?.id ?? null,
        name,
        // Never persist the patient-specific engraving name into a shared preset.
        configuration: stripPresetCustomName((form.deep ?? {}) as Record<string, unknown>),
      });
    },
    // A failed insert used to stop the spinner and show nothing: the name
    // stayed in the box and the user had no idea the preset was not saved.
    onMutate: () => setSaveError(null),
    onError: () => setSaveError(PRESET_SAVE_ERROR),
    onSuccess: () => {
      setSavingName(null);
      void queryClient.invalidateQueries({
        queryKey: prescriptionPresetQueries.prescriptionPresetsKeys.byClinic(clinicId),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      prescriptionPresetQueries.deletePrescriptionPreset(supabase, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: prescriptionPresetQueries.prescriptionPresetsKeys.byClinic(clinicId),
      });
    },
  });

  // Apply semantics for both presets and pathologies are SNAPSHOT + PATCH:
  // the merge target is always the captured baseline (pre-assistant
  // form.deep), so switching from option A to option B swaps A's
  // contribution for B's while the user's pre-default cards survive.
  // The parent's onApplyDeep does a total deep replacement (not a spread),
  // so we hand it the fully-merged next state.
  //
  // Snapshot semantics: on the FIRST apply (applied === null) we clone
  // form.deep so the user's baseline selections can be restored on clear
  // and reused as the merge base for every subsequent switch. Once a
  // preset/pathology is active, switching to another one must NOT
  // re-snapshot (the snapshot represents the pre-assistant baseline).
  //
  // The double structuredClone is intentional: one for the merge target
  // (deepMergeDeepState mutates a shallow copy), one for the snapshot
  // state (kept immutable from the form's perspective).
  const applyPreset = (id: string, name: string, config: Record<string, unknown>) => {
    const baselineRaw = applied === null ? form.deep ?? {} : snapshotBeforeApply ?? {};
    const baseline = structuredClone(baselineRaw) as PrescriptionDeepState;
    if (applied === null) {
      setSnapshotBeforeApply(structuredClone(form.deep ?? {}) as PrescriptionDeepState);
    }
    // Strip any legacy custom_name so applying a preset never injects a stray
    // patient/engraving name (the field stays whatever the operator set).
    onApplyDeep(
      deepMergeDeepState(baseline, stripPresetCustomName(config ?? {}) as PrescriptionDeepState),
    );
    setApplied({ kind: 'preset', id, label: name });
  };

  const applyPathology = (id: string, label: string, patch: PrescriptionDeepState) => {
    const baselineRaw = applied === null ? form.deep ?? {} : snapshotBeforeApply ?? {};
    const baseline = structuredClone(baselineRaw) as PrescriptionDeepState;
    if (applied === null) {
      setSnapshotBeforeApply(structuredClone(form.deep ?? {}) as PrescriptionDeepState);
    }
    onApplyDeep(deepMergeDeepState(baseline, patch));
    setApplied({ kind: 'pathology', id, label });
  };

  const handleClear = React.useCallback(() => {
    onApplyDeep(snapshotBeforeApply ?? {});
    setApplied(null);
    setSnapshotBeforeApply(null);
  }, [onApplyDeep, snapshotBeforeApply]);

  // Live preview of the modifications layered onto the form. Top cover is
  // intentionally NOT chipped - it's a per-prescription default the user
  // sets (or keeps) on the form itself, not driven by the pathology layer.
  const mods = summarizeModifications(form.deep);
  const allChips = mods;

  // Click-to-jump: prefer the parent-provided handler (it opens the card's
  // section first), fall back to a plain scroll when used outside the
  // wizard (e.g. read-only previews).
  const jumpToCard = (cardId: string | undefined) => {
    if (!cardId) return;
    if (onJumpToCard) {
      onJumpToCard(cardId);
      return;
    }
    const el = document.getElementById(cardId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.add('ring-2', 'ring-brand-400', 'ring-offset-2');
    window.setTimeout(() => {
      el.classList.remove('ring-2', 'ring-brand-400', 'ring-offset-2');
    }, 1600);
  };

  if (collapsed) return null;

  return (
    <Card className="sticky top-20 self-start">
      <CardBody className="p-0">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border-subtle">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="h-4 w-4 shrink-0 text-brand-600" />
            <div className="text-sm font-semibold text-text-primary">
              Prescription assistant
            </div>
          </div>
          <button
            type="button"
            onClick={() => onCollapsedChange(true)}
            aria-label="Hide assistant"
            className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text-primary px-2 py-1 rounded-md hover:bg-sand-100"
          >
            <PanelRightClose className="h-4 w-4 rtl-flip" />
            Hide
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-xs text-text-secondary leading-snug">
            Load a saved prescription or pathology default to fill the form
            in one click.
          </p>

          {/* Tab switch */}
          <div role="tablist" className="inline-flex p-0.5 bg-sand-100 rounded-md text-xs w-full">
            <TabButton
              active={tab === 'presets'}
              onClick={() => setTab('presets')}
              icon={<Layers className="h-3.5 w-3.5" />}
              label="My presets"
            />
            <TabButton
              active={tab === 'pathology'}
              onClick={() => setTab('pathology')}
              icon={<Stethoscope className="h-3.5 w-3.5" />}
              label="By pathology"
            />
          </div>

          {tab === 'presets' ? (
            <PresetsTab
              loading={presetsQuery.isLoading}
              presets={presetsQuery.data ?? []}
              applied={applied?.kind === 'preset' ? applied : null}
              onApply={applyPreset}
              onClear={handleClear}
              onDelete={(id) => deleteMutation.mutate(id)}
              deletingId={deleteMutation.isPending ? deleteMutation.variables ?? null : null}
              savingName={savingName}
              onStartSave={() => setSavingName('')}
              onCancelSave={() => setSavingName(null)}
              onChangeName={setSavingName}
              onConfirmSave={(name) => createMutation.mutate(name)}
              saving={createMutation.isPending}
              saveError={saveError}
              canSave={!!form.deep && Object.keys(form.deep).length > 0 && !!clinicId}
            />
          ) : (
            <PathologyTab
              presets={presets}
              applied={applied?.kind === 'pathology' ? applied : null}
              onApply={applyPathology}
              onClear={handleClear}
            />
          )}

          {/* Currently selected - clickable chips that jump to the card */}
          <div className="rounded-lg border border-border-subtle bg-sand-50 p-3 space-y-2">
            <div className="text-xs font-semibold text-text-primary">
              Currently selected
            </div>
            {allChips.length === 0 ? (
              <p className="text-xs text-text-muted">
                Nothing selected yet. Apply a preset or pathology, or fill the
                form directly.
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {allChips.map((m) => (
                  <SelectionChip
                    key={`${m.cardId ?? m.label}-${m.label}`}
                    label={m.label}
                    sides={m.sides}
                    onClick={() => jumpToCard(m.cardId)}
                    clickable={!!m.cardId}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Button rendered next to the stepper card when the assistant is hidden.
 * Exported as its own component so NewOrderPage can place it without
 * coupling to the panel's internals.
 */
export function PrescriptionAssistantOpenButton({
  onOpen,
}: {
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="inline-flex items-center gap-2 rounded-xl border border-brand-200 bg-brand-50 hover:bg-brand-100 px-3 py-2 text-sm font-medium text-brand-800 transition-colors shrink-0"
      aria-label="Open prescription assistant"
    >
      <Sparkles className="h-4 w-4" />
      Prescription assistant
      <ChevronRight className="h-4 w-4 rtl-flip" />
    </button>
  );
}

/* -------------------------------------------------------------------------- */

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      role="tab"
      type="button"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex-1 px-2.5 py-1.5 rounded inline-flex items-center justify-center gap-1.5 transition-colors',
        active
          ? 'bg-surface-elevated text-text-primary shadow-sm font-medium'
          : 'text-text-muted hover:text-text-primary',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function AppliedChip({
  label,
  onClear,
}: {
  label: string;
  onClear: () => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border-2 border-brand-500 bg-brand-50 px-3 py-2">
      <Check className="h-4 w-4 text-brand-700 shrink-0" />
      <span className="text-sm font-medium text-brand-800 truncate flex-1">{label}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label="Clear selection"
        className="text-brand-700 hover:text-brand-900 hover:bg-brand-100 rounded-full p-0.5"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function PickerDropdown({
  placeholder,
  options,
  onSelect,
}: {
  placeholder: string;
  options: Array<{ value: string; label: string; hint?: string }>;
  onSelect: (value: string) => void;
}) {
  return (
    <select
      value=""
      onChange={(e) => {
        if (e.target.value) onSelect(e.target.value);
      }}
      className="w-full h-9 rounded-md border border-border-default bg-surface-elevated px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
          {o.hint ? ` - ${o.hint}` : ''}
        </option>
      ))}
    </select>
  );
}

/** Selection chip used in the "Currently selected" preview. */
function SelectionChip({
  label,
  sides,
  onClick,
  clickable,
}: {
  label: string;
  sides: ModEntry['sides'];
  onClick: () => void;
  clickable: boolean;
}) {
  const sideTag =
    sides === 'both'
      ? 'Both'
      : sides === 'left'
        ? 'L only'
        : sides === 'right'
          ? 'R only'
          : null;
  return (
    <button
      type="button"
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={cn(
        'w-full flex items-center justify-between gap-2 rounded-md border border-border-subtle bg-white px-2.5 py-1.5 text-xs text-text-primary text-start transition-colors',
        clickable
          ? 'hover:border-brand-400 hover:bg-brand-50 cursor-pointer'
          : 'cursor-default',
      )}
      title={clickable ? 'Open this card' : undefined}
    >
      <span className="font-medium truncate min-w-0">{label}</span>
      {sideTag ? (
        <span className="text-[10px] uppercase tracking-wide text-text-muted shrink-0">
          {sideTag}
        </span>
      ) : null}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  My presets tab                                                            */
/* -------------------------------------------------------------------------- */

function PresetsTab({
  loading,
  presets,
  applied,
  onApply,
  onClear,
  onDelete,
  deletingId,
  savingName,
  onStartSave,
  onCancelSave,
  onChangeName,
  onConfirmSave,
  saving,
  saveError,
  canSave,
}: {
  loading: boolean;
  presets: Array<{ id: string; name: string; configuration: Record<string, unknown> }>;
  applied: AppliedSelection | null;
  onApply: (id: string, name: string, config: Record<string, unknown>) => void;
  onClear: () => void;
  onDelete: (id: string) => void;
  deletingId: string | null;
  savingName: string | null;
  onStartSave: () => void;
  onCancelSave: () => void;
  onChangeName: (v: string) => void;
  onConfirmSave: (name: string) => void;
  saving: boolean;
  saveError: string | null;
  canSave: boolean;
}) {
  const appliedPreset =
    applied && presets.find((p) => p.id === applied.id);
  return (
    <div className="space-y-3">
      {loading ? (
        <p className="text-xs text-text-muted">Loading your presets…</p>
      ) : applied && appliedPreset ? (
        <div className="space-y-2">
          <AppliedChip label={applied.label} onClear={onClear} />
          <button
            type="button"
            onClick={() => onDelete(appliedPreset.id)}
            disabled={deletingId === appliedPreset.id}
            className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-error-600 disabled:opacity-40"
          >
            {deletingId === appliedPreset.id ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Trash2 className="h-3 w-3" />
            )}
            Delete this preset
          </button>
        </div>
      ) : presets.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border-subtle bg-sand-50 p-3 text-center">
          <p className="text-xs font-medium text-text-secondary">
            No saved presets for this clinic yet
          </p>
          <p className="text-xs text-text-muted mt-1">
            Build a prescription, then save it below to reuse it on future orders.
          </p>
        </div>
      ) : (
        <PickerDropdown
          placeholder="Choose a saved preset…"
          options={presets.map((p) => ({ value: p.id, label: p.name }))}
          onSelect={(id) => {
            const p = presets.find((x) => x.id === id);
            if (p) onApply(p.id, p.name, p.configuration);
          }}
        />
      )}

      {/* Save current as preset */}
      {savingName === null ? (
        <Button
          size="sm"
          variant="outline"
          onClick={onStartSave}
          disabled={!canSave}
          startIcon={<BookmarkPlus className="h-3.5 w-3.5" />}
          className="w-full"
        >
          Save current as preset
        </Button>
      ) : (
        <div className="space-y-2 rounded-lg border border-brand-200 bg-brand-50 p-2.5">
          <input
            type="text"
            autoFocus
            value={savingName}
            onChange={(e) => onChangeName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && savingName.trim()) onConfirmSave(savingName.trim());
              if (e.key === 'Escape') onCancelSave();
            }}
            placeholder="Preset name (e.g. Diabetic offload)"
            className="w-full h-8 rounded-md border border-border-default bg-surface-elevated px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={() => savingName.trim() && onConfirmSave(savingName.trim())}
              disabled={!savingName.trim() || saving}
              isLoading={saving}
            >
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancelSave} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {saveError ? (
        <p role="alert" className="text-xs text-error-600" data-testid="preset-save-error">
          {saveError}
        </p>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  By pathology tab                                                          */
/* -------------------------------------------------------------------------- */

function PathologyTab({
  presets,
  applied,
  onApply,
  onClear,
}: {
  presets: ReadonlyArray<{ id: string; label: string; hint: string; apply: PrescriptionDeepState }>;
  applied: AppliedSelection | null;
  onApply: (id: string, label: string, patch: PrescriptionDeepState) => void;
  onClear: () => void;
}) {
  if (applied) {
    return (
      <div className="space-y-2">
        <AppliedChip label={applied.label} onClear={onClear} />
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <PickerDropdown
        placeholder="Choose a pathology default…"
        options={presets.map((p) => ({
          value: p.id,
          label: p.label,
        }))}
        onSelect={(id) => {
          const p = presets.find((x) => x.id === id);
          if (p) onApply(p.id, p.label, p.apply);
        }}
      />
      <p className="text-[11px] text-text-muted leading-relaxed">
        Defaults layer onto the current prescription. Review and adjust each
        modification before submitting.
      </p>
    </div>
  );
}
